import React, { useCallback, useEffect, useMemo, useState } from "react"
import styled from "styled-components"
import {
    MdContentCopy,
    MdCancel,
    MdReplay,
    MdDescription,
    MdMoreVert,
    MdDeleteOutline,
    MdFileDownload,
} from "react-icons/md"
import { IconButton, Menu, MenuItem, ListItemIcon, ListItemText } from "@mui/material"
import ModernMUITable from "../../../../Components/ModernMUITable"
import { useModalProvider } from "../../../../Providers/ModalProvider"
import { useEndpointProvider } from "../../../../Providers/EndpointProvider"
import { useBackendLinksProvider } from "../../../../Providers/BackendLinksProvider"
import { useAuthProvider } from "../../../../Providers/AuthProvider"
import MobergSnackbar from "../../../../Components/MobergSnackbar/MobergSnackbar"
import { localizeDate, humanReadableSize } from "../../../../Computation/utilFunctions"
import LogsModal from "./LogsModal"
import DeleteConfirmModal from "./DeleteConfirmModal"


// Status palette — colors lifted from Pages/RemoteMonitoring/components/StatusBadge.jsx
// to match the View MCP Devices visual family. `fill` is only used by the IN_PROGRESS
// gradient and is the same RGB triple at higher alpha.
const STATUS_PALETTE = {
    PENDING: {
        // Neutral pill body + blue dot — signals "live, queued, about to start"
        // without blending with CANCELLED (which is fully grey).
        label: "Pending",
        bg: "var(--color-hover-neutral)",
        dotColor: "#2196F3",
        textColor: "var(--text-secondary-color)",
    },
    IN_PROGRESS: {
        label: "In progress",
        bg: "rgba(33, 150, 243, 0.15)",
        fill: "rgba(33, 150, 243, 0.45)",
        dotColor: "#2196F3",
        textColor: "#2196F3",
    },
    CANCELLING: {
        label: "Cancelling…",
        bg: "rgba(255, 152, 0, 0.15)",
        dotColor: "#FF9800",
        textColor: "#FF9800",
    },
    CANCELLED: {
        label: "Cancelled",
        bg: "var(--color-hover-neutral)",
        dotColor: "#757575",
        textColor: "var(--text-secondary-color)",
    },
    COMPLETED: {
        label: "Completed",
        bg: "rgba(76, 175, 80, 0.15)",
        dotColor: "#4CAF50",
        textColor: "#4CAF50",
    },
    FAILED: {
        label: "Failed",
        bg: "rgba(244, 67, 54, 0.15)",
        dotColor: "#F44336",
        textColor: "#F44336",
    },
    DELETED: {
        // Red — destructive-action visual, matches the Delete menu item color.
        label: "Deleted",
        bg: "rgba(244, 67, 54, 0.15)",
        dotColor: "#F44336",
        textColor: "#F44336",
    },
}


const PillCellWrap = styled.div`
    display: flex;
    align-items: center;
    height: 100%;
`

const PillContainer = styled.div`
    display: inline-flex;
    align-items: center;
    padding: 4px 12px;
    border-radius: 12px;
    font-size: 13px;
    font-weight: 600;
    line-height: 1.4;
    position: relative;
    overflow: hidden;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
    width: fit-content;
    box-sizing: border-box;
`

// Small neutral pill used to render the export's file format (HDF5/EDF/CSV)
// in the table. Same shape language as the status pill but lower contrast so
// it reads as metadata, not state.
const FormatPill = styled.span`
    display: inline-flex;
    align-items: center;
    padding: 2px 10px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.3px;
    color: var(--color-accent-interactive);
    background: rgba(33, 150, 243, 0.10);
    border: 1px solid rgba(33, 150, 243, 0.25);
    line-height: 1.4;
    font-variant-numeric: tabular-nums;
`

const RowActionsCell = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
`

// MUI Menu portals to document.body and uses theme.zIndex.modal (1300) by default.
// Our modal overlay sits at the same level, so the menu can render *under* the
// modal and clicks register on the overlay instead of the menu items. Forcing
// the menu's portal to z-index 100000 keeps it on top regardless of modal stack.
// (Per-patient tab is not inside a modal, but the high z-index is harmless there.)
const ROW_MENU_Z_INDEX = 100000

const PillContent = styled.span`
    position: relative;
    z-index: 1;
    display: inline-flex;
    align-items: center;
    gap: 8px;
`

const Dot = styled.span`
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
    background: ${({ color }) => color};
`

// IN_PROGRESS variant of Dot — a soft expanding aura behind the dot conveys
// "this thing is alive" even when the progress percentage hasn't moved in a
// few seconds (which happens during long basenames). CSS-only, transform +
// opacity so the GPU does the work.
const PulsingDot = styled.span`
    position: relative;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
    background: ${({ color }) => color};

    &::before {
        content: '';
        position: absolute;
        inset: -2px;
        border-radius: 50%;
        background: ${({ color }) => color};
        opacity: 0.55;
        animation: et-pill-dot-pulse 1.6s ease-in-out infinite;
        z-index: -1;
    }

    @keyframes et-pill-dot-pulse {
        0%   { transform: scale(0.85); opacity: 0.55; }
        70%  { transform: scale(1.9);  opacity: 0; }
        100% { transform: scale(0.85); opacity: 0; }
    }
`

const SmallSpinner = styled.span`
    display: inline-block;
    width: 10px;
    height: 10px;
    border: 2px solid currentColor;
    border-top-color: transparent;
    border-radius: 50%;
    animation: et-pill-spin 0.8s linear infinite;
    flex-shrink: 0;
    @keyframes et-pill-spin {
        to { transform: rotate(360deg); }
    }
`

// Determinate fill — solid color. Width transitions smoothly when progress
// ticks.
const ProgressFill = styled.span`
    position: absolute;
    inset: 0;
    width: ${({ $pct }) => $pct}%;
    background: ${({ $fill }) => $fill};
    transition: width 350ms cubic-bezier(0.22, 0.61, 0.36, 1);
`

// Indeterminate variant — used while pct === 0 so the pill doesn't look
// frozen for the seconds between IN_PROGRESS arriving and the first
// basename_complete event landing. Slim band sweeps left → right via
// transform: translateX so the animation runs on the compositor thread,
// not the main thread — stays smooth even when React is busy rendering
// progress updates elsewhere.
//
// Math: bar is 32% of parent's width. translateX is relative to the bar's
// OWN width. Sweeping the bar from offscreen-left to offscreen-right means
// going from translateX(-100%) to translateX(312.5%) (since 100% of parent
// = 100/32 × 100% of bar = 312.5%).
const IndeterminateBar = styled.span`
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 32%;
    background: ${({ $fill }) => $fill};
    border-radius: inherit;
    will-change: transform;
    transform: translateX(-100%);
    animation: et-pill-indeterminate 1.3s linear infinite;

    @keyframes et-pill-indeterminate {
        0%   { transform: translateX(-100%); }
        100% { transform: translateX(312.5%); }
    }
`

export const ExportStatusPill = ({ row, isCancelling }) => {
    const effectiveStatus = isCancelling ? "CANCELLING" : row.status
    const palette = STATUS_PALETTE[effectiveStatus] || STATUS_PALETTE.PENDING
    const isLoading = effectiveStatus === "IN_PROGRESS"
    const pct = isLoading
        ? Math.max(0, Math.min(1, row.progress || 0)) * 100
        : 0

    const labelText = isLoading
        ? `${palette.label} · ${Math.round(pct)}%`
        : palette.label

    return (
        <PillCellWrap>
            <PillContainer
                style={{ background: palette.bg, color: palette.textColor }}
                title={isLoading ? (row.current_basename || "Converting...") : undefined}
            >
                {isLoading && pct > 0 && (
                    <ProgressFill $pct={pct} $fill={palette.fill} />
                )}
                {isLoading && pct === 0 && (
                    <IndeterminateBar $fill={palette.fill} />
                )}
                <PillContent>
                    {isCancelling
                        ? <SmallSpinner />
                        : isLoading
                            ? <PulsingDot color={palette.dotColor} />
                            : <Dot color={palette.dotColor} />}
                    <span>{labelText}</span>
                </PillContent>
            </PillContainer>
        </PillCellWrap>
    )
}

function RowActionsMenu({ row, isCancelling, isRetrying, isDeleting, isDownloading, canManage, onCancel, onRetry, onCopyOutput, onViewLogs, onDelete, onDownload }) {
    const [anchorEl, setAnchorEl] = useState(null)
    const open = Boolean(anchorEl)

    const handleOpen = (e) => {
        e.stopPropagation()
        setAnchorEl(e.currentTarget)
    }
    const handleClose = (e) => {
        if (e) e.stopPropagation?.()
        setAnchorEl(null)
    }
    const wrap = (fn) => (e) => {
        handleClose(e)
        fn(row)
    }

    // DELETED rows are read-only — only View logs remains in the menu.
    // Mutating actions (Cancel / Retry / Delete) require the export:manage
    // permission; view-only users see Download / Copy / View logs only.
    const isDeleted = row.status === "DELETED"
    const canCancel = !isDeleted && canManage && (row.status === "PENDING" || row.status === "IN_PROGRESS")
    const canRetry = !isDeleted && canManage && row.status === "FAILED"
    const canDownload = !isDeleted && row.status === "COMPLETED" && !!row.output_path
    const canCopy = !isDeleted && row.status === "COMPLETED" && !!row.output_path
    const canDelete = !isDeleted && canManage && ["COMPLETED", "FAILED", "CANCELLED"].includes(row.status)

    return (
        <>
            <IconButton
                size="small"
                onClick={handleOpen}
                aria-label="Row actions"
                sx={{ color: "var(--text-primary-color)" }}
            >
                <MdMoreVert size={20} />
            </IconButton>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                onClick={(e) => e.stopPropagation()}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                slotProps={{ paper: { sx: { zIndex: ROW_MENU_Z_INDEX } } }}
                sx={{ zIndex: ROW_MENU_Z_INDEX }}
            >
                {canCancel && (
                    <MenuItem
                        onClick={wrap(onCancel)}
                        disabled={isCancelling}
                        sx={{
                            color: "#F44336",
                            "& .MuiListItemIcon-root": { color: "#F44336" },
                            "&:hover": { backgroundColor: "rgba(244, 67, 54, 0.08)" },
                        }}
                    >
                        <ListItemIcon><MdCancel size={20} /></ListItemIcon>
                        <ListItemText>{isCancelling ? "Cancelling…" : "Cancel"}</ListItemText>
                    </MenuItem>
                )}
                {canRetry && (
                    <MenuItem
                        onClick={wrap(onRetry)}
                        disabled={isRetrying || typeof row.data_object_id !== "number"}
                    >
                        <ListItemIcon><MdReplay size={20} /></ListItemIcon>
                        <ListItemText>{isRetrying ? "Retrying…" : "Retry"}</ListItemText>
                    </MenuItem>
                )}
                {canDownload && (
                    <MenuItem onClick={wrap(onDownload)} disabled={isDownloading}>
                        <ListItemIcon><MdFileDownload size={20} /></ListItemIcon>
                        <ListItemText>{isDownloading ? "Downloading…" : "Download"}</ListItemText>
                    </MenuItem>
                )}
                <MenuItem onClick={wrap(onViewLogs)}>
                    <ListItemIcon><MdDescription size={20} /></ListItemIcon>
                    <ListItemText>View logs</ListItemText>
                </MenuItem>
                {canCopy && (
                    <MenuItem onClick={wrap(onCopyOutput)}>
                        <ListItemIcon><MdContentCopy size={20} /></ListItemIcon>
                        <ListItemText
                            primaryTypographyProps={{
                                fontSize: 13,
                                color: "var(--text-secondary-color)",
                            }}
                        >
                            Copy file path
                        </ListItemText>
                    </MenuItem>
                )}
                {canDelete && (
                    <MenuItem
                        onClick={wrap(onDelete)}
                        disabled={isDeleting}
                        sx={{
                            color: "#F44336",
                            "& .MuiListItemIcon-root": { color: "#F44336" },
                            "&:hover": { backgroundColor: "rgba(244, 67, 54, 0.08)" },
                        }}
                    >
                        <ListItemIcon><MdDeleteOutline size={20} /></ListItemIcon>
                        <ListItemText>{isDeleting ? "Deleting…" : "Delete"}</ListItemText>
                    </MenuItem>
                )}
            </Menu>
        </>
    )
}


function formatStartedAt(iso) {
    if (!iso) return "—"
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return "—"
    try {
        return localizeDate(d)
    } catch (e) {
        return d.toLocaleString()
    }
}

function basenameOf(p) {
    if (!p) return ""
    const idx = p.lastIndexOf("/")
    return idx >= 0 ? p.slice(idx + 1) : p
}

// The worker writes absolute paths like /mnt/CNSArchive/exports/job_<id>/<file>.
// Only the `exports/job_<id>/<file>` suffix is portable across deployments —
// each environment mounts the export storage at its own root (Z:\… on Windows,
// /Volumes/… on Mac, etc.). Strip everything before the `exports/job_<id>/`
// segment so the copied path appends cleanly to the user's local mount.
function toRelativePath(p) {
    if (!p) return ""
    const m = p.match(/(exports\/job_\d+\/.+)$/)
    return m ? m[1] : p
}

function fileTypeOf(row) {
    // Prefer the authoritative server-side `format` field. Falls back to
    // inferring from the output_path extension for any pre-chunk-4.0 rows that
    // might still be in flight. Final fallback is "HDF5" (the historical default).
    if (typeof row?.format === "string" && row.format.length > 0) {
        return row.format
    }
    const base = basenameOf(row?.output_path)
    const dot = base.lastIndexOf(".")
    if (dot > 0) {
        const ext = base.slice(dot + 1).toLowerCase()
        if (ext === "h5" || ext === "hdf5") return "HDF5"
        return ext.toUpperCase()
    }
    return "HDF5"
}


/**
 * Shared, presentation-focused exports table + per-row kebab actions.
 *
 * Owned by the parent: items, loading, refresh trigger, and any upstream live-update
 * machinery (Socket.IO in the global Tracker modal, polling in the per-patient tab).
 *
 * Owned here: column rendering, transient action state (cancelling/retrying/deleting/
 * downloading ids), action handlers, optimistic mutations via setItems, snackbar
 * notifications, confirm-delete modal, view-logs modal.
 *
 * The handlers call setItems(updater) to do optimistic updates so the UI doesn't wait
 * for the next refresh cycle. onRefresh is called after destructive actions to force
 * an immediate re-fetch where the parent supports it.
 */
export default function ExportsListView({ items, setItems, onRefresh, loading, emptyState }) {
    const { createModal } = useModalProvider()
    const endpointProvider = useEndpointProvider()
    const { LINKS } = useBackendLinksProvider()
    const authProvider = useAuthProvider()
    const canManage = !!authProvider.permissions?.['export:manage']

    const [actionToast, setActionToast] = useState({ open: false, message: "", severity: "error" })
    // Client-only sets driving immediate "Cancelling..." / disabled-Retry visual states.
    // Cleared when the server-side row reaches a terminal status (see effects below).
    const [cancellingIds, setCancellingIds] = useState(() => new Set())
    const [retryingIds, setRetryingIds] = useState(() => new Set())
    const [deletingIds, setDeletingIds] = useState(() => new Set())
    const [downloadingIds, setDownloadingIds] = useState(() => new Set())

    const copyToClipboard = useCallback((text) => {
        if (!text) return
        navigator.clipboard?.writeText(text).then(() => {
            setActionToast({
                open: true,
                message: "Output path copied to clipboard.",
                severity: "success",
            })
        }).catch(() => {})
    }, [])

    const handleCancel = useCallback(async (item) => {
        const id = item.id
        setCancellingIds(prev => {
            const next = new Set(prev)
            next.add(id)
            return next
        })
        try {
            const cancelEndpoint = {
                LINK: `${LINKS.DATA.EXPORTS.START.LINK}/${id}`,
                OUTPUT_TYPE: "json",
                REQUIRES_TOKEN: true,
            }
            await endpointProvider.patch(cancelEndpoint, { status: "CANCELLED" })
            onRefresh?.()
        } catch (err) {
            setCancellingIds(prev => {
                const next = new Set(prev)
                next.delete(id)
                return next
            })
            setActionToast({
                open: true,
                message: `Cancel failed: ${err?.message || "unknown error"}`,
                severity: "error",
            })
        }
    }, [LINKS, endpointProvider, onRefresh])

    const handleRetry = useCallback(async (item) => {
        const id = item.id
        if (typeof item.data_object_id !== "number") {
            setActionToast({
                open: true,
                message: "Cannot retry: original data_object_id is missing.",
                severity: "error",
            })
            return
        }
        setRetryingIds(prev => {
            const next = new Set(prev)
            next.add(id)
            return next
        })
        try {
            // Retry preserves the original format. The user can always launch a
            // brand-new export with a different format via the Export modal.
            const retryFormat = item.format || "HDF5"
            const result = await endpointProvider.post(
                LINKS.DATA.EXPORTS.START,
                { data_object_id: item.data_object_id, format: retryFormat }
            )
            const newJobId = result?.job_id
            setActionToast({
                open: true,
                message: `Retry started — job #${newJobId}. Track in this list.`,
                severity: "success",
            })
            if (typeof newJobId === "number") {
                setItems(prev => [
                    {
                        id: newJobId,
                        status: "PENDING",
                        progress: 0,
                        format: retryFormat,
                        patient_id: item.patient_id,
                        patient_identifier: item.patient_identifier,
                        data_object_id: item.data_object_id,
                        source_path: item.source_path,
                        output_path: null,
                        started_at: null,
                        ended_at: null,
                        failure_reason: null,
                    },
                    ...prev,
                ])
            }
            onRefresh?.()
        } catch (err) {
            setActionToast({
                open: true,
                message: `Retry failed: ${err?.message || "unknown error"}`,
                severity: "error",
            })
        } finally {
            setRetryingIds(prev => {
                const next = new Set(prev)
                next.delete(id)
                return next
            })
        }
    }, [LINKS, endpointProvider, setItems, onRefresh])

    const openLogsModal = useCallback((row) => {
        createModal(<LogsModal row={row} />, { escClose: true, clickOutsideClose: true })
    }, [createModal])

    const handleDeleteConfirm = useCallback(async (row) => {
        const id = row.id
        setDeletingIds(prev => {
            const next = new Set(prev)
            next.add(id)
            return next
        })
        try {
            const deleteEndpoint = {
                LINK: `${LINKS.DATA.EXPORTS.START.LINK}/${id}`,
                OUTPUT_TYPE: "text",
                REQUIRES_TOKEN: true,
            }
            await endpointProvider.del(deleteEndpoint, {})
            // Server returned 204. Flip the local row to DELETED immediately so the
            // kebab/output cell re-render without waiting for the next refresh.
            // The row stays in the list — audit trail.
            setItems(prev => prev.map(item =>
                item.id === id ? { ...item, status: "DELETED", output_path: null } : item
            ))
            setActionToast({
                open: true,
                message: `Job #${id} deleted.`,
                severity: "success",
            })
            onRefresh?.()
        } catch (err) {
            setActionToast({
                open: true,
                message: `Delete failed: ${err?.message || err || "unknown error"}`,
                severity: "error",
            })
        } finally {
            setDeletingIds(prev => {
                const next = new Set(prev)
                next.delete(id)
                return next
            })
        }
    }, [LINKS, endpointProvider, setItems, onRefresh])

    const handleDeleteClick = useCallback((row) => {
        createModal(
            <DeleteConfirmModal row={row} onConfirm={() => handleDeleteConfirm(row)} />,
            { escClose: true, clickOutsideClose: true }
        )
    }, [createModal, handleDeleteConfirm])

    const handleDownload = useCallback(async (row) => {
        const id = row.id
        setDownloadingIds(prev => {
            const next = new Set(prev)
            next.add(id)
            return next
        })
        try {
            // Two-step browser-native download:
            // 1. POST /v1/exports/<id>/download_url → server mints a 60s ticket
            //    and returns a path-only URL containing it.
            // 2. We prepend LINKS.QUERY and click a hidden anchor, which lets
            //    the browser navigate to the URL and stream the response to
            //    its native download bar.
            const urlEndpoint = {
                LINK: `${LINKS.DATA.EXPORTS.START.LINK}/${id}/download_url`,
                OUTPUT_TYPE: "json",
                REQUIRES_TOKEN: true,
            }
            const result = await endpointProvider.post(urlEndpoint, {})
            const path = result?.url
            if (typeof path !== "string" || !path) {
                throw new Error("Server did not return a download URL.")
            }
            const fullUrl = `${LINKS.QUERY}${path}`

            const link = document.createElement("a")
            link.href = fullUrl
            link.setAttribute("download", "")
            document.body.appendChild(link)
            link.click()
            link.parentNode.removeChild(link)

            setActionToast({
                open: true,
                message: `Download started for job #${id}. Check your browser's download bar.`,
                severity: "success",
            })
        } catch (err) {
            setActionToast({
                open: true,
                message: `Download failed: ${err?.message || err || "unknown error"}`,
                severity: "error",
            })
        } finally {
            // Browser owns the stream now — clear the local spinner immediately so
            // subsequent clicks aren't blocked. The native download UI is the
            // ongoing affordance from here on.
            setDownloadingIds(prev => {
                const next = new Set(prev)
                next.delete(id)
                return next
            })
        }
    }, [LINKS, endpointProvider])

    // Drop transient cancelling state once a row reaches a terminal status.
    useEffect(() => {
        if (cancellingIds.size === 0) return
        const stale = []
        cancellingIds.forEach(id => {
            const row = items.find(j => j.id === id)
            if (!row) return
            if (["CANCELLED", "COMPLETED", "FAILED", "DELETED"].includes(row.status)) {
                stale.push(id)
            }
        })
        if (stale.length > 0) {
            setCancellingIds(prev => {
                const next = new Set(prev)
                stale.forEach(id => next.delete(id))
                return next
            })
        }
    }, [items, cancellingIds])

    // Drop transient deleting state once a row reaches DELETED.
    useEffect(() => {
        if (deletingIds.size === 0) return
        const stale = []
        deletingIds.forEach(id => {
            const row = items.find(j => j.id === id)
            if (!row) return
            if (row.status === "DELETED") {
                stale.push(id)
            }
        })
        if (stale.length > 0) {
            setDeletingIds(prev => {
                const next = new Set(prev)
                stale.forEach(id => next.delete(id))
                return next
            })
        }
    }, [items, deletingIds])

    const columns = useMemo(() => [
        {
            field: "status",
            headerName: "Status",
            minWidth: 190,
            flex: 0.35,
            disableColumnMenu: true,
            sortable: false,
            renderCell: (params) => (
                <ExportStatusPill
                    row={params.row}
                    isCancelling={cancellingIds.has(params.row.id)}
                />
            ),
        },
        {
            field: "patient_identifier",
            headerName: "Patient ID",
            minWidth: 300,
            flex: 0.8,
            disableColumnMenu: true,
            valueGetter: (value, row) =>
                row.patient_identifier || row.patient_id || "—",
        },
        {
            field: "source_path",
            headerName: "File",
            flex: 1.4,
            minWidth: 240,
            disableColumnMenu: true,
            valueGetter: (value, row) => basenameOf(row.source_path),
            renderCell: (params) => (
                <span title={params.row.source_path}>
                    {basenameOf(params.row.source_path)}
                </span>
            ),
        },
        {
            field: "file_type",
            headerName: "File type",
            width: 90,
            disableColumnMenu: true,
            sortable: true,
            valueGetter: (value, row) => fileTypeOf(row),
            renderCell: (params) => (
                <FormatPill>{fileTypeOf(params.row)}</FormatPill>
            ),
        },
        {
            field: "output_size_bytes",
            headerName: "Size",
            width: 100,
            disableColumnMenu: true,
            sortable: true,
            valueGetter: (value, row) => {
                const bytes = row.output_size_bytes
                return typeof bytes === "number" && bytes > 0
                    ? humanReadableSize(bytes)
                    : "—"
            },
        },
        {
            field: "started_at",
            headerName: "Started",
            minWidth: 220,
            flex: 0.6,
            disableColumnMenu: true,
            valueGetter: (value, row) => formatStartedAt(row.started_at),
        },
        {
            field: "actions",
            headerName: "Actions",
            width: 100,
            hideable: false,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            align: "center",
            headerAlign: "center",
            renderCell: (params) => (
                <RowActionsCell>
                    <RowActionsMenu
                        row={params.row}
                        isCancelling={cancellingIds.has(params.row.id)}
                        isRetrying={retryingIds.has(params.row.id)}
                        isDeleting={deletingIds.has(params.row.id)}
                        isDownloading={downloadingIds.has(params.row.id)}
                        canManage={canManage}
                        onCancel={handleCancel}
                        onRetry={handleRetry}
                        onCopyOutput={(r) => copyToClipboard(toRelativePath(r.output_path))}
                        onViewLogs={openLogsModal}
                        onDelete={handleDeleteClick}
                        onDownload={handleDownload}
                    />
                </RowActionsCell>
            ),
        },
    ], [cancellingIds, retryingIds, deletingIds, downloadingIds, canManage, copyToClipboard, handleCancel, handleRetry, openLogsModal, handleDeleteClick, handleDownload])

    const tableToolbarProps = {
        createButton: { isShow: false },
        deleteButton: { isShow: false },
        filterButton: { isShow: false },
        exportButton: { isShow: false },
        columnButton: { isShow: false },
        searchInput: {
            isShow: true,
            placeholder: "Search exports by patient, file, or status...",
        },
        mainTable: true,
    }

    const isEmpty = !loading && items.length === 0

    return (
        <>
            {isEmpty && emptyState ? (
                emptyState
            ) : (
                <ModernMUITable
                    columns={columns}
                    tableData={items}
                    rowCount={items.length}
                    tableToolbarProps={tableToolbarProps}
                    supportsPageSize={false}
                    pageSizeOptions={[]}
                    isRowSelectable={() => false}
                    isCheckboxSelection={false}
                    checkboxSelection={false}
                    height="100%"
                    paginationMode="client"
                    sortingMode="client"
                    loading={loading}
                    initialState={{
                        pagination: {
                            paginationModel: { page: 0, pageSize: 10 },
                        },
                    }}
                />
            )}
            <MobergSnackbar
                open={actionToast.open}
                onClose={() => setActionToast(prev => ({ ...prev, open: false }))}
                message={actionToast.message}
                severity={actionToast.severity}
            />
        </>
    )
}
